import axiosInstance from './axiosConfig';

export interface PartnerResource {
  _id: string;
  title: string;
  description?: string;
  category: string;
  fileName: string;
  originalName: string;
  fileType: string;
  mimeType: string;
  fileSize: number;
  s3Key: string;
  thumbnailKey?: string;
  tags: string[];
  featured: boolean;
  isPublished: boolean;
  sortOrder: number;
  downloadCount: number;
  uploadedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  tenant?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceCategory {
  id: string;
  name: string;
  icon: string;
  count: number;
}

export interface ResourcesResponse {
  success: boolean;
  data: {
    resources: PartnerResource[];
    categories: ResourceCategory[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface UploadResourceData {
  file: File;
  title: string;
  description?: string;
  category: string;
  tags?: string;
  featured?: boolean;
  isGlobal?: boolean;
}

// Get all resources with optional filters
export const getResources = async (params?: {
  category?: string;
  featured?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<ResourcesResponse> => {
  const response = await axiosInstance.get('/partner-resources', { params });
  return response.data;
};

// Get a single resource by ID
export const getResource = async (id: string): Promise<{ success: boolean; data: PartnerResource }> => {
  const response = await axiosInstance.get(`/partner-resources/${id}`);
  return response.data;
};

// Get categories with counts (only non-empty by default)
export const getCategories = async (includeEmpty = false): Promise<{ success: boolean; data: ResourceCategory[] }> => {
  const response = await axiosInstance.get('/partner-resources/categories', {
    params: includeEmpty ? { includeEmpty: 'true' } : undefined
  });
  return response.data;
};

// All available categories for upload form
export const ALL_CATEGORIES = [
  { id: 'demo-materials', name: 'Demo Materials', icon: 'PlayCircle' },
  { id: 'sales-collateral', name: 'Sales Collateral', icon: 'TrendingUp' },
  { id: 'training', name: 'Training', icon: 'School' },
  { id: 'technical-docs', name: 'Technical Docs', icon: 'Description' }
] as const;

// Check if current user is sales admin
export const checkAdminRole = async (): Promise<{ success: boolean; data: { isSalesAdmin: boolean } }> => {
  const response = await axiosInstance.get('/partner-resources/check-admin');
  return response.data;
};

// Upload a new resource (sales_admin only)
export const uploadResource = async (data: UploadResourceData): Promise<{ success: boolean; data: PartnerResource; message: string }> => {
  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('title', data.title);
  if (data.description) formData.append('description', data.description);
  formData.append('category', data.category);
  if (data.tags) formData.append('tags', data.tags);
  if (data.featured !== undefined) formData.append('featured', String(data.featured));
  if (data.isGlobal !== undefined) formData.append('isGlobal', String(data.isGlobal));

  const response = await axiosInstance.post('/partner-resources', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Update a resource (sales_admin only)
export const updateResource = async (
  id: string,
  data: Partial<{
    title: string;
    description: string;
    category: string;
    tags: string;
    featured: boolean;
    isPublished: boolean;
    sortOrder: number;
    isGlobal: boolean;
  }>
): Promise<{ success: boolean; data: PartnerResource; message: string }> => {
  const response = await axiosInstance.put(`/partner-resources/${id}`, data);
  return response.data;
};

// Delete a resource (sales_admin only)
export const deleteResource = async (id: string): Promise<{ success: boolean; message: string }> => {
  const response = await axiosInstance.delete(`/partner-resources/${id}`);
  return response.data;
};

// Download a resource (tracks download count)
export const downloadResource = async (id: string): Promise<{ success: boolean; data: { downloadUrl: string; fileName: string } }> => {
  const response = await axiosInstance.post(`/partner-resources/${id}/download`);
  return response.data;
};

// Get raw content of a text/markdown file (for in-app viewing)
export const getResourceContent = async (id: string): Promise<{ success: boolean; data: { content: string; title: string; mimeType: string } }> => {
  const response = await axiosInstance.get(`/partner-resources/${id}/content`);
  return response.data;
};

// Helper to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to get file icon based on type
export const getFileIcon = (fileType: string): string => {
  const icons: Record<string, string> = {
    pdf: 'PictureAsPdf',
    word: 'Description',
    excel: 'TableChart',
    powerpoint: 'Slideshow',
    image: 'Image',
    video: 'VideoLibrary',
    text: 'Article',
    other: 'InsertDriveFile',
  };
  return icons[fileType] || icons.other;
};

// Helper to get category icon
export const getCategoryIcon = (categoryId: string): string => {
  const icons: Record<string, string> = {
    'demo-materials': 'PlayCircle',
    'sales-collateral': 'TrendingUp',
    'training': 'School',
    'technical-docs': 'Description',
  };
  return icons[categoryId] || 'Folder';
};

export default {
  getResources,
  getResource,
  getCategories,
  checkAdminRole,
  uploadResource,
  updateResource,
  deleteResource,
  downloadResource,
  getResourceContent,
  formatFileSize,
  getFileIcon,
  getCategoryIcon,
};
